import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Fix default marker icons (CRA/webpack asset path issue)
const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { if (lat != null && lng != null) map.setView([lat, lng], map.getZoom() < 10 ? 11 : map.getZoom()); }, [lat, lng]);
  return null;
}

function ClickHandler({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`, {
      headers: { "Accept": "application/json" },
    });
    const d = await r.json();
    const a = d.address || {};
    return {
      city: a.city || a.town || a.village || a.county || "",
      state: a.state || a.region || "",
      country: a.country || "",
      zip: a.postcode || "",
      metro: a.county || a.state_district || "",
    };
  } catch (e) { return null; }
}

export default function MapPicker({ lat, lng, onLocation, testidPrefix = "map" }) {
  const [pos, setPos] = useState(lat != null && lng != null ? [lat, lng] : null);
  const [busy, setBusy] = useState(false);
  const center = pos || [39.5, -98.35]; // US center default (not hard-coded to a launch city)

  const applyPick = async (la, ln) => {
    setPos([la, ln]);
    setBusy(true);
    const geo = await reverseGeocode(la, ln);
    setBusy(false);
    // Store only approximate (2-decimal) coordinates for privacy; backend also rounds.
    const approxLat = Math.round(la * 100) / 100;
    const approxLng = Math.round(ln * 100) / 100;
    onLocation({ latitude: approxLat, longitude: approxLng, ...(geo || {}) });
  };

  const useDevice = () => {
    if (!navigator.geolocation) return toast.error("Location not supported on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setBusy(false); applyPick(p.coords.latitude, p.coords.longitude); toast.success("Approximate location set"); },
      (err) => {
        setBusy(false);
        if (err.code === 1) toast.message("Location permission denied", { description: "No problem — just type your city instead." });
        else toast.error("Couldn't get your location. Type your city instead.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-[#9E978E] flex items-center gap-1"><MapPin size={12} className="text-[#D4AF37]" /> Tap the map to set an approximate location</span>
        <button type="button" data-testid={`${testidPrefix}-use-device-btn`} onClick={useDevice} disabled={busy}
          className="rounded-full border border-[#D4AF37]/30 text-[#D4AF37] text-xs px-3 py-1.5 flex items-center gap-1.5 hover:bg-[#541224]/30">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Crosshair size={13} />} Use my location
        </button>
      </div>
      <div className="rounded-xl overflow-hidden border border-[#D4AF37]/20" style={{ height: 240 }}>
        <MapContainer center={center} zoom={pos ? 11 : 3} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickHandler onPick={applyPick} />
          {pos && <Marker position={pos} icon={markerIcon} />}
          {pos && <Recenter lat={pos[0]} lng={pos[1]} />}
        </MapContainer>
      </div>
      <p className="text-[11px] text-[#9E978E]">Only an approximate area is stored for distance search. Your exact pin is never shown to others.</p>
    </div>
  );
}
